import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { EstoqueService } from "../../Services/EstoqueService";
import { AppProvider } from "../../ContextApi/AppProvider";
import { useContext } from "react";
import IFiltroPesquisaProdutos from "../Shared/Interfaces/FiltroPesquisaProdutos";
import EditIcon from "@mui/icons-material/Edit";
import Produto from "../Shared/Interfaces/Produto";
import ModalEditarProduto from "./ModalEditarProduto";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatToBRL } from "../../Utils/utils";
import DataItem from "../Shared/Interfaces/DataItem";
import { TipoCadastro } from "../Shared/Enums/TipoCadastro";
import { CadastroService } from "../../Services/CadastrosService";
import { RelatorioProdutos, TipoProdutoRelatorio } from "../Shared/Interfaces/RelatorioProdutos";
import HistoryIcon from '@mui/icons-material/History';
import ModalHistoricoProduto from './ModalHistoricoProduto';
import SearchIcon from '@mui/icons-material/Search';
import colors from "../../theme/colors";
import ModalCadastrarProduto from "./ModalCadastrarProduto";
import ModalCadastrarKit from "./ModalCadastrarKit";
import type { KitResponseDto } from "../Shared/Interfaces/Kit";
import { KitsService } from "../../Services/KitsService";

const estoqueService = new EstoqueService();
const kitsService = new KitsService();
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

type EstoqueGridRow =
  | {
      kind: "PRODUTO";
      id: number;
      descricao: string;
      valor: number;
      quantidade: number;
      raw: Produto;
    }
  | {
      kind: "KIT";
      id: number;
      descricao: string;
      valor: number;
      quantidade: number;
      ativo: boolean;
      raw: KitResponseDto;
    };

interface Column {
  id: "id" | "kind" | "descricao" | "quantidade" | "valor" | "ativo";
  label: string;
  minWidth?: number;
  align?: "right";
  format?: (value: number) => string;
}

const columns: readonly Column[] = [
  { id: "id", label: "Id", minWidth: 20 },
  { id: "kind", label: "Tipo", minWidth: 80 },
  { id: "descricao", label: "Descrição", minWidth: 200 },
  {
    id: "quantidade",
    label: "Qtd / Itens",
    minWidth: 120,
    align: "right",
  },
  {
    id: "valor",
    label: "Valor",
    minWidth: 120,
    align: "right",
    format: (value: number) => formatToBRL(value), // Apply BRL formatting
  },
  {
    id: "ativo",
    label: "Ativo",
    minWidth: 80,
    align: "right",
  },
];

export default function GerenciarEstoque() {
  const contextApi = useContext(AppProvider);
  const cadastroService = new CadastroService();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [medidas, setMedidas] = React.useState<DataItem[]>([]);
  const [marcas, setMarcas] = React.useState<DataItem[]>([]);
  const [tiposDePneus, setTiposDePneus] = React.useState<DataItem[]>([]);
  const [tiposDeProdutos, setTiposDeProdutos] = React.useState<DataItem[]>([]);
  const [produtosRows, setProdutosRows] = React.useState<Produto[]>([]);
  const [kitsRows, setKitsRows] = React.useState<KitResponseDto[]>([]);
  const [relatorioProdutos, setRelatorioProdutos] =
    React.useState<TipoProdutoRelatorio[]>([]);
  const [mostrarZerados, setMostrarZerados] = React.useState(false);

  const handleChangeMostrarZerados = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setMostrarZerados(event.target.checked);
  };
  const initialStateProduto: Produto = {
    id: 0,
    descricao: "",
    medida: "",
    tipoPneu: "",
    tipoProduto: "",
    marca: "",
    lote: 0,
    codigo: 0,
    quantidade: 0,
    valor: 0,
  };
  const [produtoEdicao, setProdutoEdicao] =
    React.useState<Produto>(initialStateProduto);
  const [formPesquisar, setFormPesquisar] =
    React.useState<IFiltroPesquisaProdutos>({
      medida: "",
      tipoPneu: "",
      tipoProduto: "",
      marca: "",
      descricao: "",
    });

  const resetFormPesquisar = () => {
    setFormPesquisar({
      medida: "",
      tipoPneu: "",
      tipoProduto: "",
      marca: "",
      descricao: "",
    });
  };

  const showFiltered = (row: EstoqueGridRow) => {
    if (row.kind === "KIT") return true;
    return mostrarZerados ? row.quantidade == 0 : row.quantidade > 0;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const PesquisarPor = (formPesquisar: IFiltroPesquisaProdutos) => {
    const filtroPesquisa: IFiltroPesquisaProdutos = {
      medida: formPesquisar.medida,
      tipoPneu: formPesquisar.tipoPneu,
      tipoProduto: formPesquisar.tipoProduto,
      marca: formPesquisar.marca,
      descricao: formPesquisar.descricao,
    };

    const descricao = (formPesquisar.descricao || "").trim();
    if (descricao) {
      kitsService
        .buscarPor(descricao)
        .then((response) => {
          if (response.status === 200) {
            const data = response.data;
            setKitsRows(Array.isArray(data) ? data : []);
          } else {
            setKitsRows([]);
          }
        })
        .catch((error) => {
          const status = error?.response?.status;
          if (status === 404) {
            setKitsRows([]);
            return;
          }
          const data = error?.response?.data;
          const msg =
            typeof data === "string"
              ? data
              : data && typeof data === "object"
                ? (data as any).message || JSON.stringify(data)
                : error?.message || "Ocorreu um erro interno. Tente novamente mais tarde.";
          contextApi.showMessage(msg, true, "error");
          setKitsRows([]);
        });
    } else {
      fetchKits();
    }

    estoqueService
      .postItem("/api/Produtos/BuscarPor", filtroPesquisa)
      .then((response) => {
        if (response.status == 200) {
          setProdutosRows(response.data);
        } else {
          contextApi.showMessage(
            "Erro ao Pesquisar, tente novamente!",
            true,
            "error"
          );
        }
      })
      .catch((error) => {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const msg =
          typeof data === "string"
            ? data
            : data && typeof data === "object"
              ? (data as any).message || JSON.stringify(data)
              : error?.message || "Ocorreu um erro interno. Tente novamente mais tarde.";

        if (status === 404) {
          setProdutosRows([]);
          contextApi.showMessage("Nenhum produto encontrado para o filtro informado.", true, "info");
          return;
        }

        contextApi.showMessage(msg || "Erro ao Pesquisar, tente novamente!", true, "error");
      });
  };

  const getMedidas = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Medidas}`
      );
      setMedidas(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };
  const getTipoDePneu = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDePneus}`
      );
      setTiposDePneus(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getTipoDeProduto = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.TiposDeProdutos}`
      );
      setTiposDeProdutos(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getMarcas = async () => {
    try {
      const response = await cadastroService.getAll(
        `/api/Cadastros/Buscar?tipoCadastro=${TipoCadastro.Marcas}`
      );
      setMarcas(response.data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const getAllCadastros = async () => {
    getMedidas();
    getTipoDePneu();
    getTipoDeProduto();
    getMarcas();
  };

  const handleDeleteItem = (item: Produto) => {
    const deleteItem: Produto = {
      id: item.id,
      descricao: item.descricao,
      medida: item.medida,
      tipoPneu: item.tipoPneu,
      tipoProduto: item.tipoProduto,
      marca: item.marca,
      quantidade: item.quantidade,
      codigo: item.codigo,
      lote: item.lote,
      valor: item.valor,
    };
    estoqueService
      .deleteItem(`/api/Produtos/Deletar/${deleteItem.id}`)
      .then((response) => {
        if (response.status == 204) {
          contextApi.showMessage("item excluído com sucesso!", true, "success");
          fetchProdutos();
          fetchRelatorioProdutos();
        } else {
          contextApi.showMessage(
            "Erro ao excluir o item, tente novamente!",
            true,
            "error"
          );
        }
      })
      .catch((error) => {
        const data = error?.response?.data;
        const msg =
          typeof data === "string"
            ? data
            : data && typeof data === "object"
              ? (data as any).message || JSON.stringify(data)
              : error?.message || "Ocorreu um erro interno. Tente novamente mais tarde.";
        contextApi.showMessage(msg, true, "error");
      });
  };

  const handleDeleteKit = (kit: KitResponseDto) => {
    kitsService
      .deletar(kit.id)
      .then((response) => {
        if (response.status == 200) {
          contextApi.showMessage("Kit excluído com sucesso!", true, "success");
          fetchKits();
        } else {
          contextApi.showMessage(
            "Erro ao excluir o kit, tente novamente!",
            true,
            "error"
          );
        }
      })
      .catch((error) => {
        const data = error?.response?.data;
        const msg =
          typeof data === "string"
            ? data
            : data && typeof data === "object"
              ? (data as any).message || JSON.stringify(data)
              : error?.message || "Ocorreu um erro interno. Tente novamente mais tarde.";
        contextApi.showMessage(msg, true, "error");
      });
  };

  const handleInputPesquisar = (
    fieldName: keyof IFiltroPesquisaProdutos,
    value: string
  ) => {
    setFormPesquisar((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };
  const fetchProdutos = async () => {
    try {
      const response = await estoqueService.getItems(
        "/api/Produtos/Buscar?MostrarZerados=true"
      );
      const data = response.data;
      setProdutosRows(data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  const fetchKits = async () => {
    try {
      const response = await kitsService.buscar();
      const data = response.data;
      setKitsRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setKitsRows([]);
    }
  };

  const fetchRelatorioProdutos = async () => {
    try {
      const response = await estoqueService.getItems(
        "/api/Relatorios/QuantidadeProdutoPorTipo"
      );
      const data = response.data;
      setRelatorioProdutos(data);
    } catch (e) {
      console.error("Erro ao buscar dados da API:", e);
    }
  };

  /* Controles da modal de resumo */
  const [openModal, setOpenModal] = React.useState(false);
  const handleOpenModal = (item: Produto) => {
    setProdutoEdicao(item);
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

  // Histórico
  const [openHistoricoModal, setOpenHistoricoModal] = React.useState(false);
  const [historico, setHistorico] = React.useState<any[]>([]);
  const [produtoHistorico, setProdutoHistorico] = React.useState<Produto | null>(null);

  const handleOpenHistoricoModal = async (item: Produto) => {
    setProdutoHistorico(item);
    try {
      const response = await estoqueService.getHistorico(
        `/api/Historico/Produto/${item.id}`
      );
      if (response.status == 200) {
        const data = response.data;
        setHistorico(data);
      } else {
        setHistorico([]);
      }
    } catch (e) {
      setHistorico([]);
    }
    setOpenHistoricoModal(true);
  };
  const handleCloseHistoricoModal = () => setOpenHistoricoModal(false);

  // Função para calcular o total geral
  const calcularTotalGeral = () => {
    return relatorioProdutos.reduce((total, item) => total + item.quantidade, 0);
  };

  // Array de cores para os cards
  const coresCards = [
    "primary.main",
    "secondary.main",
    "success.main",
    "warning.main",
    "error.main",
    "info.main"
  ];

  React.useEffect(() => {
    getAllCadastros();
    fetchProdutos();
    fetchKits();
    fetchRelatorioProdutos();
  }, []);

  const ConcluirEdicao = (produto: Produto) => {
    estoqueService
      .putItem(`/api/Produtos/Atualizar/${produto.id}`, produto)
      .then((response) => {
        if (response.status == 204) {
          contextApi.showMessage(
            "item Atualizado com sucesso!",
            true,
            "success"
          );
          fetchProdutos();
          fetchRelatorioProdutos();
          handleCloseModal();
        } else {
          contextApi.showMessage(
            "Erro ao Atualizar o item, tente novamente!",
            true,
            "error"
          );
        }
      });
  };

  const [openCadastrarProduto, setOpenCadastrarProduto] = React.useState(false);
  const [openCadastrarKit, setOpenCadastrarKit] = React.useState(false);
  const [kitEdicao, setKitEdicao] = React.useState<KitResponseDto | null>(null);

  const abrirEditarKit = (kit: KitResponseDto) => {
    setKitEdicao(kit);
    setOpenCadastrarKit(true);
  };

  const rows: EstoqueGridRow[] = React.useMemo(() => {
    const produtos: EstoqueGridRow[] = (produtosRows || []).map((p) => ({
      kind: "PRODUTO",
      id: p.id,
      descricao: p.descricao,
      valor: Number(p.valor) || 0,
      quantidade: Number(p.quantidade) || 0,
      raw: p,
    }));

    const kits: EstoqueGridRow[] = (kitsRows || []).map((k) => ({
      kind: "KIT",
      id: k.id,
      descricao: k.nome,
      valor: Number(k.valor) || 0,
      quantidade: (k.produtos || []).length,
      ativo: !!k.ativo,
      raw: k,
    }));

    return [...kits, ...produtos].sort((a, b) => b.id - a.id);
  }, [produtosRows, kitsRows]);

  const filteredRows = rows.filter((row) => showFiltered(row));
  const pagedRows = filteredRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderEstoqueActions = (row: EstoqueGridRow) => (
    <Grid container spacing={1}>
      {row.kind === "PRODUTO" && (
        <Grid item>
          <Button
            size="small"
            color="info"
            onClick={() => handleOpenHistoricoModal(row.raw)}
            sx={{ minWidth: "auto", p: 1 }}
          >
            <HistoryIcon />
          </Button>
        </Grid>
      )}
      <Grid item>
        <Button
          size="small"
          color="primary"
          onClick={() =>
            row.kind === "PRODUTO"
              ? handleOpenModal(row.raw)
              : abrirEditarKit(row.raw)
          }
          sx={{ minWidth: "auto", p: 1 }}
        >
          <EditIcon />
        </Button>
      </Grid>
      <Grid item>
        <Button
          size="small"
          color="error"
          onClick={() =>
            row.kind === "PRODUTO"
              ? handleDeleteItem(row.raw)
              : handleDeleteKit(row.raw)
          }
          sx={{ minWidth: "auto", p: 1 }}
        >
          <DeleteIcon />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 1, sm: 2 } }}>
      {/*<h6>Gerenciar Estoque</h6>*/}
      <Paper sx={{ p: { xs: 1, sm: 2 } }}>
        <Grid container spacing={2}>
          {/* Linha 1 - Resumo do Estoque (espaço 12) */}
          <Grid item xs={12}>
            <Paper sx={{ p: 1, bgcolor: '#f8f9fa' }}>
              <h4 style={{ margin: '0 0 0 0', color: colors.bluePrimary, textAlign: 'left' }}>
                📊 Resumo do Estoque
              </h4>
              <Box sx={{ mt: 2 }}>
                {/* Cards dinâmicos para cada tipo de produto - todos em uma linha */}
                <Grid container spacing={1}>
                  {!relatorioProdutos || relatorioProdutos.length === 0 ? (
                    <p>Nenhum produto encontrado.</p>
                  ) : (
                    relatorioProdutos.map((item, index) => (
                      <Grid item xs={6} sm={4} md={1} key={item.tipoProduto}>
                        <Box
                          sx={{
                            p: 1,
                            bgcolor: coresCards[index % coresCards.length],
                            borderRadius: 2,
                            color: "white",
                            boxShadow: 2,
                            textAlign: "center",
                            height: "90%"
                          }}
                        >
                          <div style={{ fontSize: "16px", fontWeight: "bold" }}>
                            {item.quantidade}
                          </div>
                          <div style={{ fontSize: "10px" }}>{item.tipoProduto}</div>
                        </Box>
                      </Grid>
                    ))
                  )}


                  {/* Card do total geral - última coluna com tamanho md=2 */}
                  {relatorioProdutos.length > 0 && (
                    <Grid item xs={6} sm={4} md={2}>
                      <Box sx={{
                        p: 1,
                        bgcolor: "success.main",
                        borderRadius: 2,
                        color: "white",
                        boxShadow: 2,
                        textAlign: 'center',
                        height: '90%'
                      }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                          {calcularTotalGeral()}
                        </div>
                        <div style={{ fontSize: '12px' }}>Total Geral</div>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Linha 2 - Formulários (espaço 12) */}
          <Grid item xs={12}>
            {/* Seção de Cadastro */}
            <Paper sx={{ p: 1, mb: 1 }}>
              <h3 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>Cadastros</h3>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderColor: "#e0e0e0" }}>
                    <CardActionArea onClick={() => setOpenCadastrarProduto(true)}>
                      <CardContent>
                        <h4 style={{ margin: 0, color: colors.bluePrimary }}>
                          Cadastrar Produto
                        </h4>
                        <p style={{ margin: "6px 0 0 0", color: "#666" }}>
                          Realize o cadastro de produto no estoque.
                        </p>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderColor: "#e0e0e0" }}>
                    <CardActionArea
                      onClick={() => {
                        setKitEdicao(null);
                        setOpenCadastrarKit(true);
                      }}
                    >
                      <CardContent>
                        <h4 style={{ margin: 0, color: colors.bluePrimary }}>
                          Cadastrar Kit
                        </h4>
                        <p style={{ margin: "6px 0 0 0", color: "#666" }}>
                          Realize o cadastro de kit no estoque.
                        </p>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            {/* Seção de Pesquisa */}
            <Paper sx={{ p: 1, mb: 1 }}>
              <h3 style={{ margin: '0 0 20px 0', color: colors.bluePrimary }}>Pesquisar Produtos</h3>

              {/* Filtros de busca */}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    variant="outlined"
                    size="small"
                    value={formPesquisar.descricao}
                    onChange={(e) =>
                      handleInputPesquisar("descricao", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    fullWidth
                    options={medidas}
                    getOptionLabel={(option) => option.descricao}
                    size="small"
                    value={
                      medidas.find(
                        (option) => option.descricao === formPesquisar.medida
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      handleInputPesquisar("medida", newValue?.descricao || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Medida" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1.5}>
                  <Autocomplete
                    fullWidth
                    options={tiposDePneus}
                    getOptionLabel={(option) => option.descricao}
                    size="small"
                    value={
                      tiposDePneus.find(
                        (option) => option.descricao === formPesquisar.tipoPneu
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      handleInputPesquisar("tipoPneu", newValue?.descricao || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Tipo de Pneu" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1.5}>
                  <Autocomplete
                    fullWidth
                    options={tiposDeProdutos}
                    getOptionLabel={(option) => option.descricao}
                    size="small"
                    value={
                      tiposDeProdutos.find(
                        (option) => option.descricao === formPesquisar.tipoProduto
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      handleInputPesquisar("tipoProduto", newValue?.descricao || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Tipo de Produto" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <Autocomplete
                    fullWidth
                    options={marcas}
                    getOptionLabel={(option) => option.descricao}
                    size="small"
                    value={
                      marcas.find(
                        (option) => option.descricao === formPesquisar.marca
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      handleInputPesquisar("marca", newValue?.descricao || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Marca" />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={mostrarZerados}
                        onChange={handleChangeMostrarZerados}
                      />
                    }
                    label="Zerados"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={() => PesquisarPor(formPesquisar)}
                    sx={{ height: '40px' }}
                  >
                    <SearchIcon></SearchIcon>
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6} md={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={resetFormPesquisar}
                    sx={{ height: '40px' }}
                  >
                    Limpar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Linha 3 - Tabela (espaço 12) */}
          <Grid item xs={12}>
            <Paper sx={{ width: "100%", boxShadow: 3 }}>
              <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                <h3 style={{ margin: 0, color: colors.bluePrimary }}>📋 Lista de Produtos e Kits</h3>
              </Box>

              {filteredRows.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhum registro encontrado.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Mobile: cards */}
                  <Box
                    sx={{
                      display: { xs: "flex", md: "none" },
                      flexDirection: "column",
                      gap: 1.5,
                      p: { xs: 1, sm: 2 },
                    }}
                  >
                    {pagedRows.map((row) => (
                      <Paper key={`${row.kind}-${row.id}`} variant="outlined" sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {row.descricao}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {row.kind === "KIT" ? "Kit" : "Produto"} #{row.id}
                              {row.kind === "PRODUTO" && row.raw.codigo
                                ? ` · Cód. ${row.raw.codigo}`
                                : ""}
                            </Typography>
                          </Box>
                          <Typography variant="subtitle1" fontWeight={700} color="primary">
                            {formatToBRL(row.valor)}
                          </Typography>
                        </Box>

                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                          <strong>
                            {row.kind === "KIT" ? "Itens:" : "Estoque:"}
                          </strong>{" "}
                          {row.quantidade}
                        </Typography>
                        {row.kind === "KIT" && (
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Ativo:</strong> {row.ativo ? "Sim" : "Não"}
                          </Typography>
                        )}

                        <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px solid #eee" }}>
                          {renderEstoqueActions(row)}
                        </Box>
                      </Paper>
                    ))}
                  </Box>

                  {/* Desktop: tabela */}
                  <TableContainer
                    sx={{
                      display: { xs: "none", md: "block" },
                      maxHeight: 440,
                      overflowX: "auto",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          {columns.map((column) => (
                            <TableCell
                              key={column.id}
                              align={column.align}
                              sx={{
                                fontWeight: 'bold',
                                bgcolor: '#f5f5f5',
                                color: colors.bluePrimary
                              }}
                            >
                              {column.label}
                            </TableCell>
                          ))}
                          <TableCell
                            sx={{
                              fontWeight: 'bold',
                              bgcolor: '#f5f5f5',
                              color: colors.bluePrimary
                            }}
                          >
                            Ações
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pagedRows.map((row, index) => (
                          <TableRow
                            hover
                            key={`${row.kind}-${row.id}`}
                            sx={{
                              bgcolor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                              '&:hover': { bgcolor: '#e3f2fd' }
                            }}
                          >
                            {columns.map((column) => {
                              const value = (row as any)[column.id];
                              return (
                                <TableCell
                                  key={column.id}
                                  align={column.align}
                                  sx={{
                                    borderBottom: '1px solid #e0e0e0',
                                    fontSize: '14px'
                                  }}
                                >
                                  {column.id === "kind"
                                    ? row.kind === "KIT"
                                      ? "Kit"
                                      : "Produto"
                                    : column.id === "ativo"
                                      ? row.kind === "KIT"
                                        ? row.ativo
                                          ? "Sim"
                                          : "Não"
                                        : "-"
                                      : column.format && typeof value === "number"
                                        ? column.format(value)
                                        : value}
                                </TableCell>
                              );
                            })}

                            <TableCell sx={{ borderBottom: '1px solid #e0e0e0' }}>
                              {renderEstoqueActions(row)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={filteredRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  bgcolor: '#f8f9fa',
                  borderTop: '1px solid #e0e0e0'
                }}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Modal de edição */}
        <ModalEditarProduto
          open={openModal}
          onClose={handleCloseModal}
          onConcluir={ConcluirEdicao}
          produto={produtoEdicao}
        />
        {/* Modal de cadastro de produto */}
        <ModalCadastrarProduto
          open={openCadastrarProduto}
          onClose={() => setOpenCadastrarProduto(false)}
          onProdutoCriado={() => {
            fetchProdutos();
            fetchRelatorioProdutos();
          }}
        />
        {/* Modal de cadastro/edição de kit */}
        <ModalCadastrarKit
          open={openCadastrarKit}
          onClose={() => setOpenCadastrarKit(false)}
          kitEdicao={kitEdicao}
          onKitSalvo={() => {
            fetchKits();
          }}
        />
        {/* Modal de histórico */}
        <ModalHistoricoProduto
          open={openHistoricoModal}
          onClose={handleCloseHistoricoModal}
          historico={historico}
          produto={produtoHistorico}
        />
      </Paper>
    </Box>
  );
}
